// import default profile image
import { DEFAULT_PROFILE_IMAGE, DEFAULT_PROFILE_IMAGE_2 } from '../../constants/index';
import { PROFILE_IMAGE_VERSION } from './profileImageConstantsV2';

let currentDefaultProfileImage = DEFAULT_PROFILE_IMAGE;

/**
 * update current default profile image, and also update ALL img element use default profile.
 * @param {1|2} version
 */
function setDefaultImageVersion(version) {
    const prev = currentDefaultProfileImage;
    if (version === 2) {
        currentDefaultProfileImage = DEFAULT_PROFILE_IMAGE_2;
    } else {
        currentDefaultProfileImage = DEFAULT_PROFILE_IMAGE;
    }
    if (prev !== currentDefaultProfileImage) {
        /**
         * @type {NodeListOf<HTMLImageElement>}
         */
        const imgs = document.querySelectorAll(`img[src='${prev}']`);
        imgs.forEach((img) => (img.src = currentDefaultProfileImage));
    }
}

function getDefaultProfileImage() {
    return currentDefaultProfileImage;
}

/**
 * simple viewer social profile layout for profile image urls
 * @typedef {Object} ViewerSocialProfile
 * @property {string} profileImageUrlLarge
 * @property {string} profileImageUrlMedium
 * @property {string} profileImageUrlSmall
 */

/**
 * retrun DTO image by key
 * @param {SimplePublicSocialProfileV2DTO} dto
 * @param {PROFILE_IMAGE_VERSION} key
 * @returns
 */
function getImageUrl(dto, key) {
    return dto?.profileImageUrls?.[key];
}

/** VIEWER section */

/**
 * return VIEWER "large" social profile image url. 298x298.
 *
 * Return and fallback order:
 * 1. Image
 * 2. viewerSocialProfile.profileImageUrlLarge
 * 3. DEFAULT PROFILE
 *
 * @param {SimplePublicSocialProfileV2DTO} dto
 * @param {ViewerSocialProfile} [viewerSocialProfile]
 * @returns
 */
function getViewerLargeImage(dto, viewerSocialProfile) {
    return (
        getImageUrl(dto, PROFILE_IMAGE_VERSION.PROFILE) ||
        viewerSocialProfile?.profileImageUrlLarge ||
        getDefaultProfileImage()
    );
}

/**
 * return VIEWER "medium" social profile image url. 120x120.
 *
 * Return and fallback order:
 * 1. Image
 * 2. viewerSocialProfile.profileImageUrlMedium
 * 3. DEFAULT PROFILE
 *
 * @param {SimplePublicSocialProfileV2DTO} dto
 * @param {ViewerSocialProfile} [viewerSocialProfile]
 * @returns
 */
function getViewerMediumImage(dto, viewerSocialProfile) {
    return (
        getImageUrl(dto, PROFILE_IMAGE_VERSION.PROFILE_FRIEND) ||
        viewerSocialProfile?.profileImageUrlMedium ||
        getDefaultProfileImage()
    );
}

/**
 * return VIEWER "small" social profile image url. 60x60.
 *
 * Return and fallback order:
 * 1. Image
 * 2. viewerSocialProfile.profileImageUrlSmall
 * 3. DEFAULT PROFILE
 *
 * @param {SimplePublicSocialProfileV2DTO} dto
 * @param {ViewerSocialProfile} [viewerSocialProfile]
 * @returns
 */
function getViewerSmallImage(dto, viewerSocialProfile) {
    return (
        getImageUrl(dto, PROFILE_IMAGE_VERSION.PROFILE_THUMBNAIL) ||
        viewerSocialProfile?.profileImageUrlSmall ||
        getDefaultProfileImage()
    );
}

/**
 * return VIEWER "large", "framed" social profile image url. 298x298.
 *
 * Return and fallback order:
 * 1. Image with Frame
 * 2. Image
 * 3. viewerSocialProfile.profileImageUrlLarge
 * 4. DEFAULT PROFILE
 *
 * @param {SimplePublicSocialProfileV2DTO} dto
 * @param {ViewerSocialProfile} [viewerSocialProfile]
 * @returns
 */
function getViewerLargeFrameImage(dto, viewerSocialProfile) {
    return getImageUrl(dto, PROFILE_IMAGE_VERSION.FRAMED_PROFILE) || getViewerLargeImage(dto, viewerSocialProfile);
}

/**
 * return VIEWER "medium", "framed" social profile image url. 120x120.
 *
 * Return and fallback order:
 * 1. Image with Frame
 * 2. Image
 * 3. viewerSocialProfile.profileImageUrlMedium
 * 4. DEFAULT PROFILE
 *
 * @param {SimplePublicSocialProfileV2DTO} dto
 * @param {ViewerSocialProfile} [viewerSocialProfile]
 * @returns
 */
function getViewerMediumFrameImage(dto, viewerSocialProfile) {
    return getImageUrl(dto, PROFILE_IMAGE_VERSION.FRAMED_FRIEND) || getViewerMediumImage(dto, viewerSocialProfile);
}

/**
 * return VIEWER "small", "framed" social profile image url. 60x60.
 *
 * Return and fallback order:
 * 1. Image with Frame
 * 2. Image
 * 3. viewerSocialProfile.profileImageUrlSmall
 * 4. DEFAULT PROFILE
 *
 * @param {SimplePublicSocialProfileV2DTO} dto
 * @param {ViewerSocialProfile} [viewerSocialProfile]
 * @returns
 */
function getViewerSmallFrameImage(dto, viewerSocialProfile) {
    return getImageUrl(dto, PROFILE_IMAGE_VERSION.FRAMED_THUMBNAIL) || getViewerSmallImage(dto, viewerSocialProfile);
}

/**
 * return VIEWER "large", "premium icon", "framed" social profile image url. 298x298.
 *
 * Return and fallback order:
 * 1. Image with Premium Icon and Frame
 * 2. Image with Frame
 * 3. Image
 * 4. viewerSocialProfile.profileImageUrlLarge
 * 5. DEFAULT PROFILE
 *
 * @param {SimplePublicSocialProfileV2DTO} dto
 * @param {ViewerSocialProfile} [viewerSocialProfile]
 * @returns
 */
function getViewerLargeIconImage(dto, viewerSocialProfile) {
    return (
        getImageUrl(dto, PROFILE_IMAGE_VERSION.PREMIUM_PROFILE) || getViewerLargeFrameImage(dto, viewerSocialProfile)
    );
}

/**
 * return VIEWER "medium", "premium icon", "framed" social profile image url. 120x120.
 *
 * Return and fallback order:
 * 1. Image with Premium Icon and Frame
 * 2. Image with Frame
 * 3. Image
 * 4. viewerSocialProfile.profileImageUrlMedium
 * 5. DEFAULT PROFILE
 *
 * @param {SimplePublicSocialProfileV2DTO} dto
 * @param {ViewerSocialProfile} [viewerSocialProfile]
 * @returns
 */
function getViewerMediumIconImage(dto, viewerSocialProfile) {
    return (
        getImageUrl(dto, PROFILE_IMAGE_VERSION.PREMIUM_FRIEND) || getViewerMediumFrameImage(dto, viewerSocialProfile)
    );
}

/**
 * return VIEWER "small", "premium icon", "framed" social profile image url. 60x60.
 *
 * Return and fallback order:
 * 1. Image with Premium Icon and Frame
 * 2. Image with Frame
 * 3. Image
 * 4. viewerSocialProfile.profileImageUrlSmall
 * 5. DEFAULT PROFILE
 *
 * @param {SimplePublicSocialProfileV2DTO} dto
 * @param {ViewerSocialProfile} [viewerSocialProfile]
 * @returns
 */
function getViewerSmallIconImage(dto, viewerSocialProfile) {
    return (
        getImageUrl(dto, PROFILE_IMAGE_VERSION.PREMIUM_THUMBNAIL) || getViewerSmallFrameImage(dto, viewerSocialProfile)
    );
}

/** VIEWER section -- end*/

/** DTO section */

/**
 * return dto "large" social profile image url. 298x298.
 *
 * Return and fallback order:
 * 1. Image
 * 2. `fallbackImageUrl`
 * 3. DEFAULT PROFILE
 *
 * @param {SimplePublicSocialProfileV2DTO} dto
 * @param {string} [fallbackImageUrl]
 * @returns
 */
function getDtoLargeImage(dto, fallbackImageUrl) {
    return getImageUrl(dto, PROFILE_IMAGE_VERSION.PROFILE) || fallbackImageUrl || getDefaultProfileImage();
}

/**
 * return dto "medium" social profile image url. 120x120.
 *
 * Return and fallback order:
 * 1. Image
 * 2. `fallbackImageUrl`
 * 3. DEFAULT PROFILE
 *
 * @param {SimplePublicSocialProfileV2DTO} dto
 * @param {string} [fallbackImageUrl]
 * @returns
 */
function getDtoMediumImage(dto, fallbackImageUrl) {
    return getImageUrl(dto, PROFILE_IMAGE_VERSION.PROFILE_FRIEND) || fallbackImageUrl || getDefaultProfileImage();
}

/**
 * return dto "small" social profile image url. 60x60.
 *
 * Return and fallback order:
 * 1. Image
 * 2. `fallbackImageUrl`
 * 3. DEFAULT PROFILE
 *
 * @param {SimplePublicSocialProfileV2DTO} dto
 * @param {string} [fallbackImageUrl]
 * @returns
 */
function getDtoSmallImage(dto, fallbackImageUrl) {
    return getImageUrl(dto, PROFILE_IMAGE_VERSION.PROFILE_THUMBNAIL) || fallbackImageUrl || getDefaultProfileImage();
}

/**
 * return dto "large", "framed" social profile image url. 298x298.
 *
 * Return and fallback order:
 * 1. Image with Frame
 * 2. Image
 * 3. `fallbackImageUrl`
 * 4. DEFAULT PROFILE
 *
 * @param {SimplePublicSocialProfileV2DTO} dto
 * @param {string} [fallbackImageUrl]
 * @returns
 */
function getDtoLargeFrameImage(dto, fallbackImageUrl) {
    return getImageUrl(dto, PROFILE_IMAGE_VERSION.FRAMED_PROFILE) || getDtoLargeImage(dto, fallbackImageUrl);
}

/**
 * return dto "medium", "framed" social profile image url. 120x120.
 *
 * Return and fallback order:
 * 1. Image with Frame
 * 2. Image
 * 3. `fallbackImageUrl`
 * 4. DEFAULT PROFILE
 *
 * @param {SimplePublicSocialProfileV2DTO} dto
 * @param {string} [fallbackImageUrl]
 * @returns
 */
function getDtoMediumFrameImage(dto, fallbackImageUrl) {
    return getImageUrl(dto, PROFILE_IMAGE_VERSION.FRAMED_FRIEND) || getDtoMediumImage(dto, fallbackImageUrl);
}

/**
 * return dto "small", "framed" social profile image url. 60x60.
 *
 * Return and fallback order:
 * 1. Image with Frame
 * 2. Image
 * 3. `fallbackImageUrl`
 * 4. DEFAULT PROFILE
 *
 * @param {SimplePublicSocialProfileV2DTO} dto
 * @param {string} [fallbackImageUrl]
 * @returns
 */
function getDtoSmallFrameImage(dto, fallbackImageUrl) {
    return getImageUrl(dto, PROFILE_IMAGE_VERSION.FRAMED_THUMBNAIL) || getDtoSmallImage(dto, fallbackImageUrl);
}

/**
 * return dto "large", "premium icon", "framed" social profile image url. 298x298.
 *
 * Return and fallback order:
 * 1. Image with Premium Icon and Frame
 * 2. Image with Frame
 * 3. Image
 * 4. `fallbackImageUrl`
 * 5. DEFAULT PROFILE
 *
 * @param {SimplePublicSocialProfileV2DTO} dto
 * @param {string} [fallbackImageUrl]
 * @returns
 */
function getDtoLargeIconImage(dto, fallbackImageUrl) {
    return getImageUrl(dto, PROFILE_IMAGE_VERSION.PREMIUM_PROFILE) || getDtoLargeFrameImage(dto, fallbackImageUrl);
}

/**
 * return dto "medium", "premium icon", "framed" social profile image url. 120x120.
 *
 * Return and fallback order:
 * 1. Image with Premium Icon and Frame
 * 2. Image with Frame
 * 3. Image
 * 4. `fallbackImageUrl`
 * 5. DEFAULT PROFILE
 *
 * @param {SimplePublicSocialProfileV2DTO} dto
 * @param {string} [fallbackImageUrl]
 * @returns
 */
function getDtoMediumIconImage(dto, fallbackImageUrl) {
    return getImageUrl(dto, PROFILE_IMAGE_VERSION.PREMIUM_FRIEND) || getDtoMediumFrameImage(dto, fallbackImageUrl);
}

/**
 * return dto "small", "premium icon", "framed" social profile image url. 60x60.
 *
 * Return and fallback order:
 * 1. Image with Premium Icon and Frame
 * 2. Image with Frame
 * 3. Image
 * 4. `fallbackImageUrl`
 * 5. DEFAULT PROFILE
 *
 * @param {SimplePublicSocialProfileV2DTO} dto
 * @param {string} [fallbackImageUrl]
 * @returns
 */
function getDtoSmallIconImage(dto, fallbackImageUrl) {
    return getImageUrl(dto, PROFILE_IMAGE_VERSION.PREMIUM_THUMBNAIL) || getDtoSmallFrameImage(dto, fallbackImageUrl);
}

/* DTO section -- end*/

const regexp = new RegExp(/-pm\w+\.png$/);
/**
 * test `imgUrl` should not be clip circle. Returns `undefined` when not to quickly apply to `img` data-tags
 * @param {string} imgUrl
 * @returns {true|undefined}
 */
function isNoclipProfile(imgUrl) {
    if (!imgUrl.includes('/composed/')) {
        return undefined;
    }
    return regexp.test(imgUrl) || undefined;
}

const profileImageHelperV2 = {
    getImageUrl,
    getViewerLargeImage,
    getViewerMediumImage,
    getViewerSmallImage,
    getViewerLargeFrameImage,
    getViewerMediumFrameImage,
    getViewerSmallFrameImage,
    getViewerLargeIconImage,
    getViewerMediumIconImage,
    getViewerSmallIconImage,
    getDtoLargeImage,
    getDtoMediumImage,
    getDtoSmallImage,
    getDtoLargeFrameImage,
    getDtoMediumFrameImage,
    getDtoSmallFrameImage,
    getDtoLargeIconImage,
    getDtoMediumIconImage,
    getDtoSmallIconImage,
    isNoclipProfile,
    setDefaultImageVersion,
    getDefaultProfileImage
};

export default profileImageHelperV2;
